import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Shell } from "@/components/Shell";
import { imageUrl } from "@/components/ImageUpload";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Building2, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClaimVendor() {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, error: "", preview: null });
    const [form, setForm] = useState({ password: "", full_name: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { (async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/claim/${token}`);
            setState({ loading: false, error: "", preview: res.data });
        } catch (err) {
            setState({
                loading: false,
                error: err?.response?.data?.detail || "This claim link is invalid.",
                preview: null,
            });
        }
    })(); /* eslint-disable-next-line */ }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        const preview = state.preview;
        if (!preview) return;
        if (!form.password || form.password.length < 6) {
            toast.error("Please pick a password of at least 6 characters.");
            return;
        }
        if (!preview.already_has_account && !form.full_name.trim()) {
            toast.error("Tell us your full name so we can create your account.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${BACKEND_URL}/api/auth/claim-vendor`, {
                token,
                password: form.password,
                full_name: form.full_name || null,
            });
            // Persist the JWT exactly like the login + register flows
            // (`hfx_token`) so AuthContext picks it up on the next render. We
            // do a hard navigate so the protected vendor / event routes
            // re-evaluate auth state — matches existing post-login UX.
            localStorage.setItem("hfx_token", res.data.access_token);
            toast.success("Profile claimed — welcome to HealthFairX.");
            const ev = preview.event;
            const dest = ev?.event_slug
                ? `/events/${ev.event_slug}`
                : ev?.id
                ? `/events/${ev.id}`
                : "/vendor";
            window.location.href = dest;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Claim failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (state.loading) {
        return <Shell><div className="max-w-2xl mx-auto px-6 py-16 text-brand-mute" data-testid="claim-loading">Loading…</div></Shell>;
    }
    if (state.error) {
        return (
            <Shell>
                <div className="max-w-2xl mx-auto px-6 py-16" data-testid="claim-error">
                    <div className="rounded-2xl border border-brand-coral/40 bg-brand-coral/5 p-8">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="text-brand-coral mt-0.5" />
                            <div>
                                <h1 className="font-heading text-2xl font-bold">Can't claim this profile</h1>
                                <p className="text-brand-ink mt-3">{state.error}</p>
                                <p className="text-sm text-brand-mute mt-4">
                                    Need help? Email <a className="underline text-brand-forest" href="mailto:support@healthfairx.com">support@healthfairx.com</a>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Shell>
        );
    }

    const p = state.preview;
    return (
        <Shell>
            <section className="section-pad">
                <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-start">
                    <div data-testid="claim-vendor-preview">
                        <div className="label-base">Vendor invitation</div>
                        <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-2">
                            Claim your HealthFairX profile.
                        </h1>
                        <p className="mt-5 text-lg text-brand-mute">
                            An admin has prepared a profile for your business on HealthFairX. Claim it to start receiving event opportunities, manage your booth details, and respond to invitations.
                        </p>

                        <div className="mt-8 rounded-xl border border-brand-line bg-white p-6 space-y-3" data-testid="claim-vendor-card">
                            <div className="flex items-center gap-3">
                                {p.logo_url ? (
                                    <img src={imageUrl(p.logo_url)} alt="" className="w-14 h-14 rounded-lg object-cover border border-brand-line" />
                                ) : (
                                    <div className="w-14 h-14 rounded-lg bg-brand-bone inline-flex items-center justify-center"><Building2 className="text-brand-forest" /></div>
                                )}
                                <div>
                                    <div className="font-heading text-xl font-bold">{p.business_name}</div>
                                    {(p.home_city || p.home_state) && (
                                        <div className="text-sm text-brand-mute inline-flex items-center gap-1">
                                            <MapPin size={12} /> {[p.home_city, p.home_state].filter(Boolean).join(", ")}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <p className="text-xs text-brand-mute">Invitation sent to <strong className="text-brand-ink">{p.email}</strong></p>
                        </div>

                        {p.event && (
                            <div className="mt-4 rounded-xl border border-brand-forest/30 bg-brand-forest/5 p-5" data-testid="claim-vendor-event">
                                <div className="text-xs uppercase tracking-wider font-bold text-brand-forest">Linked event</div>
                                <div className="font-heading font-bold mt-1">{p.event.name}</div>
                                <div className="text-sm text-brand-mute">{p.event.event_date} · {[p.event.city, p.event.state].filter(Boolean).join(", ")}</div>
                                <p className="text-xs text-brand-mute mt-3">After you claim, we'll take you straight to this event so you can review and apply.</p>
                            </div>
                        )}
                    </div>

                    <div className="rounded-2xl border border-brand-line bg-white p-8 shadow-sm" data-testid="claim-vendor-form-card">
                        <h2 className="font-heading text-2xl font-bold">
                            {p.already_has_account ? "Sign in to claim" : "Create your account"}
                        </h2>
                        <p className="text-sm text-brand-mute mt-2">
                            {p.already_has_account
                                ? "You already have a HealthFairX account with this email. Enter your password to link it to this profile."
                                : "Set a password and we'll create your vendor account — no separate sign-up needed."}
                        </p>

                        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="claim-vendor-form" noValidate>
                            <div>
                                <label className="label-base">Email</label>
                                <input className="input-base bg-brand-bone/40 cursor-not-allowed" value={p.email} readOnly data-testid="claim-vendor-email" />
                            </div>
                            {!p.already_has_account && (
                                <div>
                                    <label className="label-base">Your full name</label>
                                    <input
                                        data-testid="claim-vendor-name"
                                        className="input-base"
                                        placeholder="Jane Doe"
                                        value={form.full_name}
                                        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                    />
                                </div>
                            )}
                            <div>
                                <label className="label-base">{p.already_has_account ? "Account password" : "Choose a password"}</label>
                                <input
                                    data-testid="claim-vendor-password"
                                    type="password"
                                    className="input-base"
                                    placeholder="At least 6 characters"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                />
                                {p.already_has_account && (
                                    <p className="text-xs text-brand-mute mt-1">
                                        Forgot it? <Link to="/forgot-password" className="underline text-brand-forest">Reset password</Link>.
                                    </p>
                                )}
                            </div>
                            <button
                                data-testid="claim-vendor-submit"
                                disabled={submitting}
                                type="submit"
                                className="w-full pill bg-brand-forest text-white px-6 py-3.5 font-semibold hover:bg-brand-forest-dark transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                            >
                                {submitting ? "Working…" : (<>
                                    <CheckCircle2 size={18} /> {p.already_has_account ? "Link profile to my account" : "Claim profile & create account"}
                                </>)}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </Shell>
    );
}
